/**
 * Bedridden Patients Form Page
 *
 * CRUD: Add / Edit Patient Form
 */

import { useState, useEffect } from 'react'
import { BedSingle, Save, X, MapPin } from 'lucide-react'
import { Sidebar, PageHeader, LoadingSpinner } from '../shared/components'
import {
  useDistricts,
  useSubdistricts,
  useDiseases,
  useBedriddenPatient,
  createBedriddenPatient,
  updateBedriddenPatient,
  type CreateBedriddenPatientData,
} from '../services/api'

interface BedriddenPatientsFormProps {
  patientId?: number | null
}

export function BedriddenPatientsForm({ patientId }: BedriddenPatientsFormProps) {
  const isEditMode = !!patientId
  const { patient, isLoading: isLoadingPatient } = useBedriddenPatient(patientId || null)
  const { data: districts, isLoading: isLoadingDistricts } = useDistricts()
  const { diseases, isLoading: isLoadingDiseases } = useDiseases()

  // Form state
  const [districtId, setDistrictId] = useState<number | null>(null)
  const [subdistrictId, setSubdistrictId] = useState<number | null>(null)
  const [initialDistrictId, setInitialDistrictId] = useState<number | null>(null) // Track initial district for edit mode
  const [selectedGroups, setSelectedGroups] = useState<number[]>([5]) // Default: ติดบ้าน/ติดเตียง
  const [type, setType] = useState<'home' | 'bed'>('home')
  const [severity, setSeverity] = useState<'high' | 'medium' | 'low'>('low')
  const [selectedDiseases, setSelectedDiseases] = useState<number[]>([])
  const [latitude, setLatitude] = useState<string>('')
  const [longitude, setLongitude] = useState<string>('')
  const [notes, setNotes] = useState<string>('')

  // UI state
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  // Load subdistricts when district changes
  const { subdistricts, isLoading: isLoadingSubdistricts } = useSubdistricts(districtId)

  // Pre-populate form in edit mode
  useEffect(() => {
    if (patient && isEditMode) {
      setDistrictId(patient.district_id)
      setSubdistrictId(patient.subdistrict_id)
      setInitialDistrictId(patient.district_id) // Store initial district to detect user changes
      setSelectedGroups(patient.vulnerable_group_ids || [5])
      setType(patient.type)
      setSeverity(patient.severity)
      setSelectedDiseases(patient.chronic_disease_ids || [])
      setLatitude(patient.lat?.toString() || '')
      setLongitude(patient.lng?.toString() || '')
      setNotes(patient.notes || '')
    }
  }, [patient, isEditMode])

  // Reset subdistrict when user manually changes district
  // BUT NOT on initial form load (edit mode)
  useEffect(() => {
    // Reset subdistrict only if:
    // 1. We have an initial district (form has loaded) OR we're in create mode
    // 2. Current district is different from initial (user changed it)
    if (initialDistrictId !== null && districtId !== initialDistrictId) {
      setSubdistrictId(null) // User changed district → reset subdistrict
    } else if (initialDistrictId === null && districtId !== null) {
      // Create mode: reset when district changes (initialDistrictId stays null)
      setSubdistrictId(null)
    }
  }, [districtId, initialDistrictId])

  const handleGroupToggle = (groupId: number) => {
    setSelectedGroups((prev) =>
      prev.includes(groupId) ? prev.filter((id) => id !== groupId) : [...prev, groupId]
    )
  }

  const handleDiseaseToggle = (diseaseId: number) => {
    setSelectedDiseases((prev) =>
      prev.includes(diseaseId) ? prev.filter((id) => id !== diseaseId) : [...prev, diseaseId]
    )
  }

  const handleCancel = () => {
    window.location.hash = 'bedridden-patients'
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    // Validation
    if (!districtId) {
      setError('กรุณาเลือกอำเภอ')
      return
    }
    if (!subdistrictId) {
      setError('กรุณาเลือกตำบล')
      return
    }

    const lat = latitude ? parseFloat(latitude) : undefined
    const lng = longitude ? parseFloat(longitude) : undefined

    if (latitude && (isNaN(lat!) || lat! < -90 || lat! > 90)) {
      setError('Latitude ไม่ถูกต้อง (ต้องอยู่ระหว่าง -90 ถึง 90)')
      return
    }

    if (longitude && (isNaN(lng!) || lng! < -180 || lng! > 180)) {
      setError('Longitude ไม่ถูกต้อง (ต้องอยู่ระหว่าง -180 ถึง 180)')
      return
    }

    if (notes.length > 5000) {
      setError('หมายเหตุต้องไม่เกิน 5000 ตัวอักษร')
      return
    }

    const formData: CreateBedriddenPatientData = {
      district_id: districtId,
      subdistrict_id: subdistrictId,
      vulnerable_group_ids: selectedGroups,
      type,
      severity,
      chronic_disease_ids: selectedDiseases.length > 0 ? selectedDiseases : undefined,
      lat: lat,
      lng: lng,
      notes: notes || undefined,
    }

    setIsSaving(true)

    try {
      if (isEditMode && patientId) {
        await updateBedriddenPatient(patientId, formData)
      } else {
        await createBedriddenPatient(formData)
      }

      setSuccess(true)

      // Redirect after 1.5 seconds
      setTimeout(() => {
        window.location.hash = 'bedridden-patients'
      }, 1500)
    } catch (err: any) {
      console.error('Form submission error:', err)
      setError(err.message || 'เกิดข้อผิดพลาดในการบันทึกข้อมูล')
      setIsSaving(false)
    }
  }

  const isLoading = isLoadingPatient || isLoadingDistricts || isLoadingDiseases

  if (isLoading) {
    return (
      <div className="flex min-h-screen bg-slate-50">
        <Sidebar currentPage="bedridden" />
        <main className="flex-1 overflow-auto">
          <PageHeader
            title={isEditMode ? 'แก้ไขผู้ป่วยติดบ้าน/ติดเตียง' : 'เพิ่มผู้ป่วยใหม่'}
            subtitle="จัดการข้อมูลผู้ป่วยรายบุคคล"
            icon={BedSingle}
          />
          <div className="p-6 flex justify-center">
            <LoadingSpinner />
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar currentPage="bedridden" />

      <main className="flex-1 overflow-auto">
        <PageHeader
          title={isEditMode ? 'แก้ไขผู้ป่วยติดบ้าน/ติดเตียง' : 'เพิ่มผู้ป่วยใหม่'}
          subtitle="จัดการข้อมูลผู้ป่วยรายบุคคล"
          icon={BedSingle}
        />

        <div className="p-6 max-w-3xl">
          {/* Success Message */}
          {success && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800">
              ✓ บันทึกข้อมูลเรียบร้อยแล้ว กำลังกลับไปหน้ารายชื่อ...
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
              ⚠️ {error}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-slate-200 shadow-sm">
            <div className="p-6 space-y-6">
              {/* District & Subdistrict */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* District */}
                <div>
                  <label htmlFor="district" className="block text-sm font-semibold text-slate-700 mb-2">
                    อำเภอ <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="district"
                    value={districtId || ''}
                    onChange={(e) => setDistrictId(e.target.value ? parseInt(e.target.value) : null)}
                    disabled={isSaving}
                    required
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-korat-navy-700 disabled:bg-slate-100 disabled:cursor-not-allowed"
                  >
                    <option value="">-- เลือกอำเภอ --</option>
                    {(districts || []).map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name_th}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Subdistrict */}
                <div>
                  <label htmlFor="subdistrict" className="block text-sm font-semibold text-slate-700 mb-2">
                    ตำบล <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="subdistrict"
                    value={subdistrictId || ''}
                    onChange={(e) => setSubdistrictId(e.target.value ? parseInt(e.target.value) : null)}
                    disabled={!districtId || isLoadingSubdistricts || isSaving}
                    required
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-korat-navy-700 disabled:bg-slate-100 disabled:cursor-not-allowed"
                  >
                    <option value="">-- เลือกตำบล --</option>
                    {(subdistricts || []).map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name_th}
                      </option>
                    ))}
                  </select>
                  {isLoadingSubdistricts && (
                    <p className="mt-1 text-xs text-slate-500">กำลังโหลดตำบล...</p>
                  )}
                </div>
              </div>

              {/* Vulnerable Groups */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  กลุ่มเปราะบาง (เลือกได้หลายกลุ่ม)
                </label>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedGroups.includes(5)}
                      onChange={() => handleGroupToggle(5)}
                      disabled={isSaving}
                      className="w-4 h-4 text-korat-navy-700 focus:ring-korat-navy-700 rounded"
                    />
                    <span className="text-sm text-slate-700">ติดบ้าน/ติดเตียง</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedGroups.includes(1)}
                      onChange={() => handleGroupToggle(1)}
                      disabled={isSaving}
                      className="w-4 h-4 text-korat-navy-700 focus:ring-korat-navy-700 rounded"
                    />
                    <span className="text-sm text-slate-700">ผู้สูงอายุ</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedGroups.includes(3)}
                      onChange={() => handleGroupToggle(3)}
                      disabled={isSaving}
                      className="w-4 h-4 text-korat-navy-700 focus:ring-korat-navy-700 rounded"
                    />
                    <span className="text-sm text-slate-700">เสี่ยงสูง</span>
                  </label>
                </div>
              </div>

              {/* Type */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  ประเภท <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="type"
                      value="home"
                      checked={type === 'home'}
                      onChange={(e) => setType(e.target.value as 'home' | 'bed')}
                      disabled={isSaving}
                      className="w-4 h-4 text-korat-navy-700 focus:ring-korat-navy-700"
                    />
                    <span className="text-sm text-slate-700">ติดบ้าน</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="type"
                      value="bed"
                      checked={type === 'bed'}
                      onChange={(e) => setType(e.target.value as 'home' | 'bed')}
                      disabled={isSaving}
                      className="w-4 h-4 text-korat-navy-700 focus:ring-korat-navy-700"
                    />
                    <span className="text-sm text-slate-700">ติดเตียง</span>
                  </label>
                </div>
              </div>

              {/* Severity */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  ระดับความเสี่ยง <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="severity"
                      value="low"
                      checked={severity === 'low'}
                      onChange={(e) => setSeverity(e.target.value as any)}
                      disabled={isSaving}
                      className="w-4 h-4 text-korat-navy-700 focus:ring-korat-navy-700"
                    />
                    <span className="text-sm text-slate-700">ต่ำ</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="severity"
                      value="medium"
                      checked={severity === 'medium'}
                      onChange={(e) => setSeverity(e.target.value as any)}
                      disabled={isSaving}
                      className="w-4 h-4 text-korat-navy-700 focus:ring-korat-navy-700"
                    />
                    <span className="text-sm text-slate-700">กลาง</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="severity"
                      value="high"
                      checked={severity === 'high'}
                      onChange={(e) => setSeverity(e.target.value as any)}
                      disabled={isSaving}
                      className="w-4 h-4 text-korat-navy-700 focus:ring-korat-navy-700"
                    />
                    <span className="text-sm text-slate-700">สูง</span>
                  </label>
                </div>
              </div>

              {/* Chronic Diseases */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  โรคเรื้อรัง (เลือกได้หลายรายการ)
                </label>
                <div className="border border-slate-300 rounded-lg p-4 max-h-64 overflow-y-auto">
                  {!diseases || diseases.length === 0 ? (
                    <p className="text-sm text-slate-500">ไม่มีข้อมูลโรคเรื้อรัง</p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {diseases.map((disease) => (
                        <label key={disease.id} className="flex items-start gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedDiseases.includes(disease.id)}
                            onChange={() => handleDiseaseToggle(disease.id)}
                            disabled={isSaving}
                            className="mt-0.5 w-4 h-4 text-korat-navy-700 focus:ring-korat-navy-700 rounded"
                          />
                          <span className="text-sm text-slate-700">{disease.name_th}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                <p className="mt-1 text-xs text-slate-500">เลือก {selectedDiseases.length} รายการ</p>
              </div>

              {/* GPS Coordinates (Optional) */}
              <div>
                <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2">
                  <MapPin className="w-4 h-4" />
                  พิกัด GPS (ไม่บังคับ)
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <input
                      type="text"
                      placeholder="Latitude (เช่น 14.9799)"
                      value={latitude}
                      onChange={(e) => setLatitude(e.target.value)}
                      disabled={isSaving}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-korat-navy-700 disabled:bg-slate-100"
                    />
                  </div>
                  <div>
                    <input
                      type="text"
                      placeholder="Longitude (เช่น 102.0977)"
                      value={longitude}
                      onChange={(e) => setLongitude(e.target.value)}
                      disabled={isSaving}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-korat-navy-700 disabled:bg-slate-100"
                    />
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label htmlFor="notes" className="block text-sm font-semibold text-slate-700 mb-2">
                  หมายเหตุ (ไม่บังคับ)
                </label>
                <textarea
                  id="notes"
                  rows={4}
                  maxLength={5000}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  disabled={isSaving}
                  placeholder="หมายเหตุเพิ่มเติม..."
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-korat-navy-700 disabled:bg-slate-100 resize-none"
                />
                <p className="mt-1 text-xs text-slate-500">
                  {notes.length} / 5000 ตัวอักษร
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-end gap-3 rounded-b-xl">
              <button
                type="button"
                onClick={handleCancel}
                disabled={isSaving}
                className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <X className="w-4 h-4 inline mr-2" />
                ยกเลิก
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className="px-4 py-2 bg-korat-navy-700 text-white rounded-lg hover:bg-korat-navy-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
              >
                {isSaving ? (
                  <>
                    <LoadingSpinner />
                    กำลังบันทึก...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    บันทึก
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  )
}
